import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SocialDraftPanel, { MediaPreview } from "@/components/nadia/SocialDraftPanel";
import { Send, Mic, Volume2, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ConversationWorkspace, { useChatWorkspace } from "@/components/nadia/ConversationWorkspace";
import StudioPreview from "@/components/nadia/StudioPreview";
import { WorkspacePanel } from "@/components/nadia/WorkspacePanel";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { Button } from "@/components/ui/button";
import { api, awaitJob, sendChat, getChatHistory, getDashboardSummary, getWallet, fetchTtsBlob, transcribeAudio } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const QUICK_PROMPTS = [
  "Crea un post per la mia attività",
  "Crea un’immagine per la mia attività",
  "Prepara un Reel",
  "Prepara una campagna pubblicitaria",
  "Come stanno andando le campagne?",
];

export default function NadiaChat() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const socialId = params.get("social") || "";
  const campaignId = params.get("campaign") || "";
  if (!user?.id) return null;
  const sessionId = `customer-${user.id}`;
  return <CustomerChat key={sessionId + socialId + campaignId} sessionId={sessionId} socialId="" initialSocialId={socialId} initialCampaignId={campaignId} />;
}

function CustomerChat({ sessionId, socialId, initialSocialId="", initialCampaignId="" }) {
  const [socialDraft, setSocialDraft] = useState(null);
  const [socialBusy, setSocialBusy] = useState(false);
  const [workspace, setWorkspace] = useState({ business: null, goal: null });
  const [messages, setMessages] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(false);
  const [historyRetry, setHistoryRetry] = useState(0);
  const [input, setInput] = useState("");
  const [mobilePane,setMobilePane] = useState("chat");
  const [wallet, setWallet] = useState(null);
  useEffect(() => { getWallet().then(setWallet).catch(() => {}); }, [sessionId]);
  const [thinking, setThinking] = useState(false);
  const [speaking, setSpeaking] = useState(null); // id of message currently playing
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const scrollRef = useRef(null);
  const latestMessageRef = useRef(null), latestReplyRef = useRef(null);
  const previewRef = useRef(null);
  const inputRef = useRef(null);
  const workbench = useChatWorkspace(sessionId, initialSocialId, initialCampaignId);

  useEffect(() => {
    if (!workbench.ready) return;
    let cancelled = false;
    setLoadingHistory(true);
    setHistoryError(false);
    Promise.all([socialId ? api.get("/social/drafts/" + socialId + "/messages").then(r=>r.data) : getChatHistory(sessionId), getDashboardSummary(), socialId ? api.get("/social/drafts/"+socialId).then(r=>r.data) : Promise.resolve(null)])
      .then(([hist, summary, draft]) => {
        setSocialDraft(draft);
        if (cancelled) return;
        const priority = summary.main_goal?.title?.trim();
        setWorkspace({ business: summary.business_name || null, goal: priority || null });
        const openingOnly = hist.length === 0 || (
          hist.length === 1 && hist[0].id === `opening-${sessionId}` && !hist[0].user
        );
        setMessages(socialId && hist.length === 0 ? [{id: "social-opening", user: null, nadia: "Prepariamo insieme il tuo contenuto, senza sponsorizzazione. Cosa vuoi condividere e con chi? Puoi allegare le tue foto o un video. La bozza resta privata finché non approvi la pubblicazione."}] : openingOnly && priority ? [{
          id: `opening-${sessionId}`,
          user: null,
          nadia: `Ciao, sono Nadia. La priorità in Panoramica è: ${priority}\nRipartiamo da questa priorità. Pubblicazione e spesa richiedono la tua approvazione.`,
        }] : hist);
      })
      .catch(() => { if (!cancelled) setHistoryError(true); })
      .finally(() => { if (!cancelled) setLoadingHistory(false); });
    return () => { cancelled = true; };
  }, [sessionId, socialId, historyRetry, workbench.ready]);

  useEffect(() => {
    const box=scrollRef.current;
    const target=latestReplyRef.current || latestMessageRef.current;
    if(box && target) {
      const top=box.scrollTop+target.getBoundingClientRect().top-box.getBoundingClientRect().top-12;
      box.scrollTo({top:Math.max(0,top),behavior:"auto"});
    } else box?.scrollTo({top:0,behavior:"auto"});
  }, [messages, thinking, socialDraft?.revision, socialId]);

  const playTts = async (id, text) => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setSpeaking(id);
      const blob = await fetchTtsBlob(text);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setSpeaking(null);
      audio.onerror = () => setSpeaking(null);
      await audio.play();
    } catch (e) {
      setSpeaking(null);
      toast.error(e.message || "Non riesco a riprodurre la voce ora");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const { text } = await transcribeAudio(blob);
          if (text) setInput((prev) => (prev ? prev + " " + text : text));
        } catch (e) {
          toast.error(e.message || "Trascrizione non riuscita");
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Non posso accedere al microfono");
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || thinking || socialBusy || workbench.busy || !workbench.ready || workbench.error || loadingHistory || historyError || (socialId && socialDraft?.status !== "draft")) return;
    setInput("");
    const tempId = `u-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempId, user: msg, nadia: null }]);
    setThinking(true);
    try {
      const res = socialId ? await api.post("/social/drafts/" + socialId + "/chat", {message: msg, revision: socialDraft?.revision || 0}, {headers: {"X-Nadia-Operation-ID": crypto.randomUUID()}}).then(r=>awaitJob(r.data)) : await sendChat(sessionId, msg);
      if (res.social_draft) setSocialDraft(res.social_draft);
      await workbench.refresh();
      setMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, id: res.id, nadia: res.reply, campaign_draft: res.campaign_draft, proposal: res.proposal, social_preview: res.social_preview, workspace: res.workspace } : m))
      );
    } catch (error) {
      await workbench.refresh();
      const detail = error?.response?.data?.detail;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === tempId
            ? { ...m, nadia: typeof detail === "string" ? detail : "Non riesco a completare la risposta. Controlla lo storico prima di riprovare." }
            : m
        )
      );
    } finally {
      window.dispatchEvent(new Event("nadia-wallet-refresh"));
      getWallet().then(setWallet).catch(() => {});
      setThinking(false);
    }
  };

  const updateSocialDraft = async (draft) => {
    setSocialDraft(draft);
    try { const {data}=await api.get('/social/drafts/'+socialId+'/messages'); setMessages(data.length?data:[{id:'social-opening',nadia:'Raccontami cosa vuoi creare. Prepariamo tutto qui.'}]); }
    catch { toast.error('Bozza salvata. Ricarica la chat per aggiornare lo storico.'); }
  };

  return (
    <div className="nadia-studio h-full flex-1 min-h-0 min-w-0 w-full">
      <div className="nadia-mobile-switch" role="group" aria-label="Vista di lavoro"><button type="button" aria-pressed={mobilePane==="chat"} onClick={()=>setMobilePane("chat")}>Chat</button><button type="button" aria-pressed={mobilePane==="preview"} onClick={()=>setMobilePane("preview")}>Anteprima</button></div>
      <section className="nadia-chat-column flex-1 min-h-0 min-w-0 overflow-hidden flex flex-col" data-mobile-hidden={mobilePane!=="chat"}>
      <header className="shrink-0 mb-3 nadia-chat-intro">
        <p className="text-xs text-slate-400 mb-1 truncate">{workspace.business || "Nadia AI · Direttore Marketing"}</p>
        <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-semibold tracking-tight text-slate-50">Parliamone <span className="text-cyan-300">con Nadia</span></h1>
        {workspace.goal && <div className="mt-3 inline-flex max-w-full items-start gap-2 rounded-xl border border-cyan-400/15 bg-cyan-400/[0.03] px-3 py-2 text-xs text-slate-300" data-testid="chat-workspace-goal"><span className="text-cyan-200 shrink-0">Obiettivo</span><span className="break-words">{workspace.goal}</span></div>}
        <p className="mt-2 text-sm text-slate-400">Post, immagini, Reel, video e campagne. Prepariamo e rivediamo tutto qui.</p>
        {socialId && <p className="mt-2 text-xs text-cyan-200">Stai creando un contenuto per {socialDraft?.channel === "tiktok" ? "TikTok" : "Facebook"} · nessun budget pubblicitario</p>}
      </header>

      {wallet?.billing?.catalog_version && <div className="text-xs text-slate-400 mb-2" data-testid="chat-credit-notice">Chat a consumo · {Number(wallet.billing.credits_available).toLocaleString("it-IT", { maximumFractionDigits: 3 })} gettoni disponibili. La quota prenotata e non utilizzata viene restituita. Voce: 1,1 gettoni / 1.000 caratteri; trascrizione: 0,25 gettoni / minuto.</div>}
      <div className="nadia-conversation flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl border border-white/5 bg-[#0B1120]/60">
        {/* Chat header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <NadiaAvatar size="sm" thinking={thinking} speaking={!!speaking} />
            <div>
              <div className="text-sm font-heading font-semibold text-slate-100">Nadia</div>
              <div className="text-[11px] text-slate-400" data-testid="chat-status">
                {loadingHistory
                  ? "Caricamento della tua conversazione…"
                  : historyError
                  ? "Contesto non disponibile"
                  : thinking
                  ? "Sta pensando…"
                  : speaking
                  ? "Nadia sta parlando…"
                  : recording
                  ? "Ti sto ascoltando…"
                  : transcribing
                  ? "Sto capendo cosa hai detto…"
                  : "Online · pronta ad aiutarti"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {recording ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={stopRecording}
                className="h-8 w-8 text-rose-400 hover:text-rose-300"
                data-testid="stop-recording-button"
              >
                <Square size={14} className="fill-current" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={startRecording}
                disabled={transcribing || loadingHistory || historyError}
                className="h-8 w-8 text-slate-400 hover:text-cyan-300"
                data-testid="start-recording-button"
              >
                {transcribing ? <Loader2 size={14} className="animate-spin" /> : <Mic size={15} />}
              </Button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-5 space-y-5" data-chat-revision="2026-10-31-v5-light" data-testid="chat-messages">
          {loadingHistory && <p className="text-sm text-slate-400" data-testid="chat-history-loading">Caricamento della tua conversazione…</p>}
          {historyError && (
            <div role="alert" className="text-sm text-slate-400 space-y-2" data-testid="chat-history-error">
              <p>Non riesco a caricare la tua conversazione e la priorità di Panoramica. Nessun dato è stato cancellato.</p>
              <Button variant="outline" onClick={() => setHistoryRetry((n) => n + 1)} data-testid="chat-history-retry">Riprova</Button>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} ref={m.id===messages[messages.length-1]?.id?latestMessageRef:null} className="space-y-3">
              {m.user ? (
                <div className="flex justify-end">
                  <div className="max-w-[85%] sm:max-w-[70%] bg-cyan-500/10 border border-cyan-500/20 text-slate-100 px-4 py-2.5 rounded-2xl rounded-br-sm text-sm">
                    {m.user}
                  </div>
                </div>
              ) : null}
              {m.nadia ? (
                <div ref={m.id===messages[messages.length-1]?.id?latestReplyRef:null} className="flex items-start gap-3">
                  <NadiaAvatar size="sm" speaking={speaking === m.id} />
                  <div className="min-w-0 flex-1 max-w-[calc(100%-3rem)] bg-[#0F172A] border border-white/5 text-slate-100 px-4 py-3 rounded-2xl rounded-tl-sm text-sm leading-relaxed relative group">
                    {m.proposal ? <details className="mb-3" data-testid={"chat-proposal-details-" + m.id}><summary className="cursor-pointer text-xs text-cyan-200 py-1 focus-visible:outline focus-visible:outline-cyan-300">Leggi tutti i dettagli della proposta</summary><div className="mt-3 whitespace-pre-wrap break-words text-slate-300">{m.nadia}</div></details> : <span className="whitespace-pre-wrap break-words">{m.nadia}</span>}
                    {m.campaign_draft?.saved && (
                      <div className="mt-3 space-y-1" data-testid={`chat-campaign-confirmation-${m.id}`}>
                        <p className="text-xs text-slate-400 break-all" data-testid={`chat-campaign-id-${m.id}`}>ID: {m.campaign_draft.id}</p>
                        <button onClick={()=>send("Riprendi la campagna "+m.campaign_draft.id)} disabled={thinking||workbench.busy} className="text-cyan-300 hover:text-cyan-200 underline" data-testid={`chat-campaign-link-${m.id}`}>Rivedi qui nella chat</button>
                      </div>
                    )}
                    <button
                      onClick={() => playTts(m.id, m.nadia)}
                      data-testid={`play-tts-${m.id}`}
                      className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${
                        speaking === m.id
                          ? "bg-cyan-500/15 text-cyan-200 border-cyan-500/30"
                          : "bg-white/[0.03] text-slate-400 border-white/10 hover:text-cyan-200 hover:border-cyan-500/30"
                      }`}
                    >
                      <Volume2 size={10} />
                      {speaking === m.id ? "In riproduzione" : "Ascolta"}
                    </button>
                    {m.proposal && typeof m.proposal === "object" && !Array.isArray(m.proposal) && (
                      <ChatProposalCard
                        message={m}
                        disabled={thinking || loadingHistory || historyError || messages[messages.length - 1]?.id !== m.id}
                        onApprove={() => send("Salva come bozza")}
                        onModify={(title) => {
                          setInput((current) => current.trim() ? current : `Vorrei modificare la proposta «${title}»: `);
                          inputRef.current?.focus();
                        }}
                        onReject={(title) => send(`Rifiuto la proposta «${title}». Non salvarla e non eseguirla.`)}
                      />
                    )}
                    {m.social_preview?.message && <details className="mt-3 border-t border-white/10 pt-3"><summary className="text-xs text-cyan-200 cursor-pointer">Bozza salvata con questo messaggio</summary><p className="whitespace-pre-wrap mt-3">{m.social_preview.message}</p><div className="mt-3 space-y-3">{(m.social_preview.media_ids||[]).map(id=><MediaPreview key={id} id={id}/>)}</div></details>}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
          {thinking && (
            <div className="flex items-center gap-3 text-sm text-slate-400">
              <NadiaAvatar size="sm" thinking />
              <span>Nadia sta pensando…</span>
            </div>
          )}

        </div>

        {/* Quick prompts */}
        {!socialId && <div className="shrink-0 px-4 sm:px-6 py-3 border-t border-white/5 flex gap-2 overflow-x-auto">
          {QUICK_PROMPTS.map((p) => (
            <button
              key={p}
              onClick={() => send(p)}
              disabled={thinking || socialBusy || loadingHistory || historyError || (!!socialId && socialDraft?.status !== "draft")}
              data-testid={`quick-prompt-${p.substring(0, 12)}`}
              className="whitespace-nowrap text-xs px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-200 transition-colors"
            >
              {p}
            </button>
          ))}
        </div>}

        {/* Input */}
        <div className="shrink-0 p-4 border-t border-white/5" data-testid="chat-composer">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex gap-2"
          >
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loadingHistory || historyError || workbench.busy || !workbench.ready || !!workbench.error || socialBusy || (!!socialId && socialDraft?.status !== "draft")}
              placeholder="Scrivi a Nadia..."
              className="flex-1 bg-[#0B1120] border border-white/10 rounded-xl px-4 py-3 text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60 text-sm"
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!input.trim() || thinking || workbench.busy || !workbench.ready || !!workbench.error || socialBusy || loadingHistory || historyError || (!!socialId && socialDraft?.status !== "draft")}
              className="h-11 px-4 bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold rounded-xl"
              data-testid="send-chat-button"
            >
              <Send size={15} />
            </Button>
          </form>
        </div>
      </div>
      {!socialId && <details className="shrink-0 max-h-[25dvh] overflow-y-auto text-xs text-slate-400"><summary className="cursor-pointer">Contesto dell’attività</summary><WorkspacePanel /></details>}
      </section>
      <aside className="nadia-preview-column" data-testid="studio-preview-column" data-mobile-hidden={mobilePane!=="preview"} aria-label="Anteprima del lavoro con Nadia">
        <StudioPreview socialDraft={socialId?socialDraft:workbench.data?.social_draft} review={workbench.data?.ad_review} goal={workspace.goal} loading={loadingHistory||workbench.busy}>
          {!socialId && !loadingHistory && <ConversationWorkspace workbench={workbench} disabled={thinking||historyError||socialBusy} onBusy={setSocialBusy} onSend={send} onCompose={text=>{setInput(text);setMobilePane("chat");setTimeout(()=>inputRef.current?.focus(),0);}} />}
            {socialId && socialDraft && !loadingHistory && <div ref={previewRef}><SocialDraftPanel draft={socialDraft} onChange={updateSocialDraft} disabled={thinking||historyError} onBusy={setSocialBusy}/></div>}
        </StudioPreview>
      </aside>
    </div>
  );
}


function ChatProposalCard({ message, disabled, onApprove, onModify, onReject }) {
  const proposal = message.proposal.proposal || message.proposal;
  const lines = message.nadia.split("\n");
  const title = typeof proposal.title === "string" && proposal.title.trim()
    ? proposal.title : lines[0].match(/^Proposta:\s*(.+)$/)?.[1] || "Proposta operativa";
  const summary = [proposal.summary, proposal.opportunity, lines[1], message.nadia]
    .find((value) => typeof value === "string" && value.trim());

  return (
    <section className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-4" aria-labelledby={`proposal-title-${message.id}`} data-testid={`chat-proposal-${message.id}`}>
      <h3 id={`proposal-title-${message.id}`} className="font-heading font-semibold text-slate-100" data-testid={`chat-proposal-title-${message.id}`}>{title}</h3>
      <p className="mt-2 text-sm text-slate-300 whitespace-pre-wrap break-words" data-testid={`chat-proposal-summary-${message.id}`}>{summary}</p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-300">
        {typeof proposal.channel === "string" && <span className="rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-1.5" data-testid={"chat-proposal-channel-" + message.id}>{proposal.channel}</span>}
        {typeof proposal.destination === "string" && <span className="rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-1.5" data-testid={"chat-proposal-destination-" + message.id}>{proposal.destination}</span>}
      </div>
      <p className="mt-3 text-xs text-slate-400" data-testid={`chat-proposal-scope-${message.id}`}>Approva salva soltanto una bozza. Nessuna pubblicazione o spesa.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" disabled={disabled} onClick={onApprove} className="bg-cyan-400 text-slate-950 hover:bg-cyan-300" data-testid={`chat-proposal-approve-${message.id}`}>Approva</Button>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => onModify(title)} data-testid={`chat-proposal-modify-${message.id}`}>Modifica</Button>
        <Button type="button" size="sm" variant="outline" disabled={disabled} onClick={() => onReject(title)} data-testid={`chat-proposal-reject-${message.id}`}>Rifiuta</Button>
      </div>
    </section>
  );
}
